import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

function EventEditorPage() {
  const params = useParams();
  const eventId = params.eventId;
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/events/' + eventId).then(r => { setEvent(r.data); setLoading(false); }).catch(() => { navigate('/dashboard'); });
  }, [eventId, navigate]);

  const save = () => {
    setSaving(true);
    api.put('/events/' + eventId, event).then(() => toast.success('Guardado')).catch(() => toast.error('Error')).finally(() => setSaving(false));
  };

  const upd = (k, v) => setEvent(p => ({ ...p, [k]: v }));

  const updArr = (k, i, fk, fv) => {
    const arr = [...(event[k] || [])];
    arr[i] = { ...arr[i], [fk]: fv };
    upd(k, arr);
  };

  const addArr = (k, obj) => upd(k, [...(event[k] || []), obj]);
  const delArr = (k, i) => upd(k, (event[k] || []).filter((_, idx) => idx !== i));

  const url = () => window.location.origin + '/invite/' + (event ? event.public_token : '');

  if (loading || !event) return <div className="min-h-screen bg-wedding-background flex items-center justify-center"><div className="animate-pulse text-wedding-primary font-heading text-2xl">Cargando...</div></div>;

  return (
    <div className="min-h-screen bg-wedding-background">
      <header className="bg-white/80 backdrop-blur-md border-b border-wedding-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
            <h1 className="font-heading text-xl text-wedding-text">{event.title}</h1>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => window.open(url(), '_blank')}><Eye className="w-4 h-4 mr-2" />Vista Previa</Button>
            <Button onClick={save} disabled={saving} className="bg-wedding-primary hover:bg-wedding-primary-dark text-white"><Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <Card className="bg-wedding-accent/30"><CardContent className="p-4 flex items-center justify-between"><div><p className="text-sm text-wedding-text-muted">URL:</p><p className="text-wedding-primary font-mono text-sm">{url()}</p></div><Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(url()); toast.success('Copiado'); }}>Copiar</Button></CardContent></Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card><CardHeader><CardTitle>Información</CardTitle></CardHeader><CardContent className="space-y-4">
            <div><Label>Título</Label><Input value={event.title || ''} onChange={e => upd('title', e.target.value)} /></div>
            <div><Label>Slogan</Label><Input value={event.slogan || ''} onChange={e => upd('slogan', e.target.value)} /></div>
            <div><Label>Descripción</Label><Textarea value={event.description || ''} onChange={e => upd('description', e.target.value)} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fuente Descripción</Label>
                <Select value={event.description_font || 'Playfair Display'} onValueChange={v => upd('description_font', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                    <SelectItem value="Georgia">Georgia</SelectItem>
                    <SelectItem value="serif">Serif (Sistema)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tamaño Texto (px)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={event.description_font_size || 18}
                    onChange={e => upd('description_font_size', parseInt(e.target.value))}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Fecha</Label><Input type="date" value={event.event_date || ''} onChange={e => upd('event_date', e.target.value)} /></div><div><Label>Hora</Label><Input type="time" value={event.event_time || ''} onChange={e => upd('event_time', e.target.value)} /></div></div>
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Lugar</CardTitle></CardHeader><CardContent className="space-y-4">
            <div><Label>Nombre</Label><Input value={event.venue_name || ''} onChange={e => upd('venue_name', e.target.value)} /></div>
            <div><Label>Dirección</Label><Input value={event.venue_address || ''} onChange={e => upd('venue_address', e.target.value)} /></div>
            <div><Label>Google Maps</Label><Input value={event.google_maps_link || ''} onChange={e => upd('google_maps_link', e.target.value)} /></div>
            <div><Label>Código de vestimenta</Label><Input value={event.dress_code || ''} onChange={e => upd('dress_code', e.target.value)} /></div>
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Diseño y Estilo</CardTitle></CardHeader><CardContent className="space-y-6">
            <div>
              <Label>Plantilla de Diseño</Label>
              <Select value={event.design_template || 'elegant'} onValueChange={v => upd('design_template', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="elegant">Elegante (Clásico)</SelectItem>
                  <SelectItem value="modern">Moderno (Minimalista)</SelectItem>
                  <SelectItem value="romantic">Romántico (Suave)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <Label>Paleta de Colores</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'Olivo Clásico', colors: { primary: '#586857', secondary: '#D4C5B0', background: '#FAFAF9', text: '#2D2D2D' } },
                  { name: 'Rosa Romántico', colors: { primary: '#9B616B', secondary: '#E5C9C5', background: '#FFF9F9', text: '#3E2723' } },
                  { name: 'Azul Noche', colors: { primary: '#1A2E35', secondary: '#9FB1BC', background: '#F4F7F8', text: '#0A1114' } },
                  { name: 'Dorado Luxury', colors: { primary: '#84623D', secondary: '#C9A66B', background: '#FDFCFB', text: '#1F1A14' } },
                ].map((p, i) => (
                  <button
                    key={i}
                    onClick={() => upd('colors', p.colors)}
                    className={`flex items-center gap-3 p-2 rounded-lg border-2 transition-all ${JSON.stringify(event.colors) === JSON.stringify(p.colors) ? 'border-wedding-primary bg-wedding-primary/5' : 'border-transparent bg-white hover:bg-gray-50'}`}
                  >
                    <div className="flex -space-x-2">
                      <div className="w-6 h-6 rounded-full border border-white" style={{ backgroundColor: p.colors.primary }} />
                      <div className="w-6 h-6 rounded-full border border-white" style={{ backgroundColor: p.colors.secondary }} />
                    </div>
                    <span className="text-xs font-medium">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Color Primario</Label><div className="flex gap-2"><Input type="color" value={event.colors?.primary || '#586857'} onChange={e => upd('colors', { ...event.colors, primary: e.target.value })} className="w-10 p-1" /><Input value={event.colors?.primary || ''} onChange={e => upd('colors', { ...event.colors, primary: e.target.value })} /></div></div>
              <div><Label>Fondo</Label><div className="flex gap-2"><Input type="color" value={event.colors?.background || '#FAFAF9'} onChange={e => upd('colors', { ...event.colors, background: e.target.value })} className="w-10 p-1" /><Input value={event.colors?.background || ''} onChange={e => upd('colors', { ...event.colors, background: e.target.value })} /></div></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Imagen Hero (Cabecera)</Label><Input value={event.hero_image || ''} onChange={e => upd('hero_image', e.target.value)} placeholder="URL de la imagen..." /></div>
              <div><Label>Video Hero (Fondo)</Label><Input value={event.hero_video || ''} onChange={e => upd('hero_video', e.target.value)} placeholder="URL del video .mp4 o YouTube..." /></div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Oscuridad del fondo (Overlay)</Label>
                <span className="text-xs text-wedding-text-muted">{Math.round((event.hero_overlay_opacity || 0.4) * 100)}%</span>
              </div>
              <input
                type="range"
                min="0" max="1" step="0.05"
                value={event.hero_overlay_opacity || 0.4}
                onChange={e => upd('hero_overlay_opacity', parseFloat(e.target.value))}
                className="w-full h-2 bg-wedding-accent rounded-lg appearance-none cursor-pointer accent-wedding-primary"
              />
            </div>
            <div><Label>Música URL</Label><Input value={event.music_url || ''} onChange={e => upd('music_url', e.target.value)} /></div>
            <div className="flex items-center justify-between"><Label>Auto-play música</Label><Switch checked={event.music_autoplay !== false} onCheckedChange={v => upd('music_autoplay', v)} /></div>
            <hr className="my-4 border-wedding-accent/30" />
            <div>
              <Label>Imagen para compartir (WhatsApp / Redes Sociales)</Label>
              <Input value={event.og_image || ''} onChange={e => upd('og_image', e.target.value)} placeholder="URL de imagen (Open Graph)..." />
              <p className="text-[10px] text-wedding-text-muted mt-1">Recomendado: 1200x630px para que se vea bien en WhatsApp.</p>
            </div>
          </CardContent></Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Frase / Versículo</CardTitle>
              <Switch checked={event.quote_enabled || false} onCheckedChange={v => upd('quote_enabled', v)} />
            </CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Texto de la Frase o Versículo</Label><Textarea value={event.quote_text || ''} onChange={e => upd('quote_text', e.target.value)} rows={3} placeholder="Ej: 'Porque donde esté vuestro tesoro, allí estará también vuestro corazón.' Mateo 6:21" /></div>
              <div><Label>Autor / Referencia</Label><Input value={event.quote_author || ''} onChange={e => upd('quote_author', e.target.value)} placeholder="Ej: Mateo 6:21 o Paulo Coelho" /></div>
              <div><Label>Imagen de Fondo (Opcional)</Label><Input value={event.quote_bg_image || ''} onChange={e => upd('quote_bg_image', e.target.value)} placeholder="URL de la imagen de fondo..." /></div>
            </CardContent>
          </Card>
          <Card><CardHeader><CardTitle>RSVP Config</CardTitle></CardHeader><CardContent className="space-y-4">
            <div><Label>Título RSVP</Label><Input value={event.rsvp_title || 'Esperamos contar contigo'} onChange={e => upd('rsvp_title', e.target.value)} /></div>
            <div className="flex items-center justify-between"><Label>Email</Label><Switch checked={event.rsvp_show_email !== false} onCheckedChange={v => upd('rsvp_show_email', v)} /></div>
            <div className="flex items-center justify-between"><Label>Nº Invitados</Label><Switch checked={event.rsvp_show_num_guests !== false} onCheckedChange={v => upd('rsvp_show_num_guests', v)} /></div>
            <div className="flex items-center justify-between"><Label>Alergias</Label><Switch checked={event.rsvp_show_allergies !== false} onCheckedChange={v => upd('rsvp_show_allergies', v)} /></div>
            <div className="flex items-center justify-between"><Label>Mensaje</Label><Switch checked={event.rsvp_show_message !== false} onCheckedChange={v => upd('rsvp_show_message', v)} /></div>
          </CardContent></Card>
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Cuentas Bancarias (Regalos)</CardTitle>
              <div className="flex items-center gap-4">
                <Switch checked={event.gifts_enabled || false} onCheckedChange={v => upd('gifts_enabled', v)} />
                <Button size="sm" variant="outline" onClick={() => addArr('bank_accounts', { bank_name: '', bank_logo: '', account_number: '', account_type: 'Ahorro', holder_name: '', id_number: '', email: '', color: '#ffffff' })}><Plus className="w-4 h-4 mr-2" />Añadir Cuenta</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div><Label>Texto de Invitación (Regalos)</Label><Textarea value={event.gifts_text || ''} onChange={e => upd('gifts_text', e.target.value)} rows={2} placeholder="Tu presencia es nuestro mejor regalo..." /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(event.bank_accounts || []).map((acc, i) => (
                  <div key={i} className="p-6 rounded-2xl border-2 space-y-4 relative transition-all" style={{ backgroundColor: acc.color + '10', borderColor: acc.color }}>
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => delArr('bank_accounts', i)}><Trash2 className="w-4 h-4" /></Button>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 md:col-span-1"><Label>Banco</Label><Input value={acc.bank_name} onChange={e => updArr('bank_accounts', i, 'bank_name', e.target.value)} placeholder="Ej: Banco Central" /></div>
                      <div className="col-span-2 md:col-span-1"><Label>Logo URL (Opcional)</Label><Input value={acc.bank_logo} onChange={e => updArr('bank_accounts', i, 'bank_logo', e.target.value)} placeholder="https://..." /></div>
                      <div className="col-span-2 md:col-span-1"><Label>Número de Cuenta (#)</Label><Input value={acc.account_number} onChange={e => updArr('bank_accounts', i, 'account_number', e.target.value)} /></div>
                      <div className="col-span-2 md:col-span-1">
                        <Label>Tipo de Cuenta</Label>
                        <Select value={acc.account_type || 'Ahorro'} onValueChange={v => updArr('bank_accounts', i, 'account_type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Ahorro">Ahorro</SelectItem>
                            <SelectItem value="Corriente">Corriente</SelectItem>
                            <SelectItem value="Cheques">Cheques</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 md:col-span-1"><Label>Dueño de cuenta</Label><Input value={acc.holder_name} onChange={e => updArr('bank_accounts', i, 'holder_name', e.target.value)} /></div>
                      <div className="col-span-2 md:col-span-1"><Label>Cédula / CI</Label><Input value={acc.id_number} onChange={e => updArr('bank_accounts', i, 'id_number', e.target.value)} /></div>
                      <div className="col-span-2 md:col-span-1"><Label>Correo</Label><Input value={acc.email} onChange={e => updArr('bank_accounts', i, 'email', e.target.value)} /></div>
                      <div className="col-span-2 md:col-span-1"><Label>Color Tarjeta</Label><div className="flex gap-2"><Input type="color" value={acc.color || '#ffffff'} onChange={e => updArr('bank_accounts', i, 'color', e.target.value)} className="w-10 p-1 h-10" /><Input value={acc.color} onChange={e => updArr('bank_accounts', i, 'color', e.target.value)} /></div></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle>Transporte</CardTitle><Switch checked={event.transport_enabled || false} onCheckedChange={v => upd('transport_enabled', v)} /></CardHeader><CardContent>
            <div><Label>Texto</Label><Textarea value={event.transport_text || ''} onChange={e => upd('transport_text', e.target.value)} rows={2} /></div>
          </CardContent></Card>

          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Programa del día (Agenda)</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArr('agenda', { time: '18:00', title: 'Nueva actividad', description: '' })}><Plus className="w-4 h-4 mr-2" />Añadir</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {(event.agenda || []).map((item, i) => (
                <div key={i} className="flex gap-4 items-start p-4 bg-wedding-accent/20 rounded-xl">
                  <div className="w-24"><Label>Hora</Label><Input value={item.time} onChange={e => updArr('agenda', i, 'time', e.target.value)} /></div>
                  <div className="flex-1"><Label>Título</Label><Input value={item.title} onChange={e => updArr('agenda', i, 'title', e.target.value)} /></div>
                  <div className="flex-1"><Label>Descripción</Label><Input value={item.description} onChange={e => updArr('agenda', i, 'description', e.target.value)} /></div>
                  <Button variant="ghost" size="icon" className="mt-6 text-red-500 hover:text-red-700" onClick={() => delArr('agenda', i)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Alojamiento</CardTitle>
              <div className="flex items-center gap-4">
                <Switch checked={event.accommodation_enabled || false} onCheckedChange={v => upd('accommodation_enabled', v)} />
                <Button size="sm" variant="outline" onClick={() => addArr('accommodation_options', { name: 'Hotel Ejemplo', description: '', address: '', link: '' })}><Plus className="w-4 h-4 mr-2" />Añadir Opción</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div><Label>Texto Introductorio</Label><Textarea value={event.accommodation_text || ''} onChange={e => upd('accommodation_text', e.target.value)} rows={2} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(event.accommodation_options || []).map((opt, i) => (
                  <div key={i} className="p-4 bg-wedding-accent/20 rounded-xl space-y-3 relative">
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => delArr('accommodation_options', i)}><Trash2 className="w-4 h-4" /></Button>
                    <div><Label>Nombre del Lugar</Label><Input value={opt.name} onChange={e => updArr('accommodation_options', i, 'name', e.target.value)} /></div>
                    <div><Label>Descripción / Oferta</Label><Input value={opt.description} onChange={e => updArr('accommodation_options', i, 'description', e.target.value)} /></div>
                    <div><Label>Dirección / Ubicación</Label><Input value={opt.address} onChange={e => updArr('accommodation_options', i, 'address', e.target.value)} /></div>
                    <div><Label>Link Web / Reserva</Label><Input value={opt.link} onChange={e => updArr('accommodation_options', i, 'link', e.target.value)} /></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle>Mensaje de WhatsApp</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl text-sm text-blue-800 flex-1 mr-4">
                  Personaliza el mensaje que se enviará por WhatsApp. Usa etiquetas:<br />
                  <code className="bg-blue-100 px-1 rounded mr-2">[familia]</code>
                  <code className="bg-blue-100 px-1 rounded mr-2">[fecha]</code>
                  <code className="bg-blue-100 px-1 rounded mr-2">[enlace]</code>
                  <code className="bg-blue-100 px-1 rounded">[novios]</code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => upd('whatsapp_template', "Hola Familia [familia] 🤍\n\nNos llena de alegría invitarlos a compartir con nosotros un día muy especial: nuestra boda 💍\n\n📅 [fecha]\n\nPueden confirmar la asistencia de toda la familia en el siguiente enlace:\n👉 [enlace]\n\n¡Esperamos contar con ustedes!\n[novios]")}
                >
                  Cargar plantilla
                </Button>
              </div>
              <Textarea
                value={event.whatsapp_template || ''}
                onChange={e => upd('whatsapp_template', e.target.value)}
                rows={10}
                placeholder="Hola Familia [familia]..."
              />
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Preguntas Frecuentes (FAQ)</CardTitle>
              <div className="flex items-center gap-4">
                <Switch checked={event.faqs_enabled || false} onCheckedChange={v => upd('faqs_enabled', v)} />
                <Button size="sm" variant="outline" onClick={() => addArr('faqs', { question: '', answer: '' })}><Plus className="w-4 h-4 mr-2" />Añadir Pregunta</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {(event.faqs || []).map((faq, i) => (
                <div key={i} className="flex gap-4 items-start p-4 bg-wedding-accent/20 rounded-xl relative">
                  <div className="flex-1 space-y-4">
                    <div><Label>Pregunta</Label><Input value={faq.question} onChange={e => updArr('faqs', i, 'question', e.target.value)} placeholder="Ej: ¿Hay parking en el lugar?" /></div>
                    <div><Label>Respuesta</Label><Textarea value={faq.answer} onChange={e => updArr('faqs', i, 'answer', e.target.value)} rows={2} placeholder="Ej: Sí, hay un parking gratuito disponible..." /></div>
                  </div>
                  <Button variant="ghost" size="icon" className="mt-6 text-red-500 hover:text-red-700" onClick={() => delArr('faqs', i)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2"><CardHeader><CardTitle>Footer</CardTitle></CardHeader><CardContent className="space-y-4">
            <div><Label>Mensaje Final</Label><Textarea value={event.footer_message || ''} onChange={e => upd('footer_message', e.target.value)} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Teléfono</Label><Input value={event.footer_phone || ''} onChange={e => upd('footer_phone', e.target.value)} /></div><div><Label>Instagram</Label><Input value={event.social_instagram || ''} onChange={e => upd('social_instagram', e.target.value)} /></div></div>
          </CardContent></Card>
        </div>
      </main>
    </div>
  );
}

export default EventEditorPage;
